/**
 * ValueLegend — what each plot reads, and the three things you can do to it.
 *
 * A chart states a plot's value only at the **right edge**: that is what the
 * axis markers are. At every other bar the number has to be traced off the line
 * by eye, and on a pane holding three overlays that is not a thing anyone does.
 *
 * **One block per pane, not one for the chart.** An RSI reading belongs in the
 * RSI pane — the same call the axis markers already make, and the only one that
 * survives a reader glancing at a pane and expecting its own numbers. Geometry
 * comes from `stackFractions`, the function the renderer stacks panes with and
 * `PaneDividers` derives its seams from; a block positioned by its own
 * arithmetic drifts off its pane the first time a divider moves.
 *
 * **Always on screen, showing the last bar at rest.** It rendered only while the
 * pointer was over a bar until the controls arrived, and then that could not
 * hold: a button that exists only under the pointer disappears as you move
 * toward it. Falling back to the last bar is what every charting tool does, is
 * strictly more useful than an empty corner, and answers the narrow shell too —
 * `mobile.md` opens with "no hover", so a phone gets last values rather than
 * nothing.
 *
 * **No date.** The crosshair already labels the X axis with the bar it is on,
 * and printing it again here would be the same value in two places that can
 * disagree after a resample.
 *
 * An HTML overlay rather than SciChart's `RolloverModifier`, which was removed
 * from this codebase once already (see charts.md). Its objections are now
 * answerable — `showRolloverLine: false` and `placementDivId` exist — but it
 * renders one tooltip box *per series*, positioned at that series' own y, and
 * this is a block of rows in a corner with buttons on them. Reaching for it
 * would mean fighting its layout for a shape the DOM gives free.
 */

import { Fragment, useLayoutEffect, useRef, useState } from "react";
import type { Pane } from "@qufolio/model";
import {
  gapAt,
  legendRows,
  legendValue,
  paneWeights,
  stackFractions,
  type PanePlan,
} from "@qufolio/chart-engine";
import { cachedCatalog } from "../api/indicators";
import { gapsForPane } from "../lib/gaps";

/**
 * What the rows can do, or `undefined` for a read-only legend.
 *
 * **Show/hide is per plot and the other two are per family**, because that is
 * what each one means. Hiding MACD's signal line is a thing anyone might want;
 * deleting it alone leaves an indicator missing a third of itself, and editing
 * its parameters *is* editing the family's — the alias encodes them, so
 * `editIndicatorParams` rewrites all three together or none.
 *
 * So × and ⚙ go on one plot per family and the eye goes on every one.
 *
 * **Which plot leads is decided here, not by the caller**, and that is the
 * point of naming the family rather than the head. A fill and a signal draw no
 * row — a band's value is its edge's printed twice, a signal's y is a lane —
 * and Chandelier's *first* plot in draw order is a fill, so a head chosen
 * upstream would land on a row that does not exist and the indicator would have
 * no way to be removed at all. The lead is the first row that survives.
 */
export interface LegendControls {
  /**
   * The indicator family a plot belongs to, or `undefined` for a base series.
   *
   * The **label** as well as the key, because a family drawing more than one row
   * is headed by its own name — the key is an internal id and nobody wants to
   * read `pane:ce:22_3_1` over a pair of stops.
   */
  familyOf: (
    plotId: string,
  ) => { key: string; label: string; removable: boolean } | undefined;
  /**
   * What the indicator picker calls this plot, when the catalog can name it.
   *
   * Resolved by the host because the catalog is fetched and the plan layer does
   * not fetch. Carried here rather than as its own prop so one
   * `appliedIndicators` pass serves both the names and the controls.
   */
  nameOf: (plotId: string) => string | undefined;
  onToggle: (plotId: string, hidden: boolean) => void;
  /**
   * Show or hide **every** plot of the family `plotId` belongs to — its fills
   * too, which draw no row here and so could not be reached any other way.
   */
  onToggleFamily: (plotId: string, hidden: boolean) => void;
  onRemove: (plotId: string) => void;
  onEdit: (plotId: string) => void;
}

export interface ValueLegendProps {
  panes: readonly Pane[];
  /** Each pane's resolved plan, by pane id — the series, in draw order. */
  planFor: (paneId: string) => PanePlan | undefined;
  /** The decoded columns the chart was fed, keyed by alias. */
  values: Readonly<Record<string, readonly number[]>>;
  /** The bar under the pointer, or `null` — at rest it reads the last one. */
  index: number | null;
  /**
   * A short qualifier to print after a plot's name, or `undefined`.
   *
   * Deliberately a callback returning an opaque string rather than anything
   * this component understands. The first caller is a seasonal reading saying
   * how many years it *actually* used — `10y -> 6` on a symbol that has six —
   * which is a fact only the frame carries and only the host has: it arrives as
   * a scalar label on the response, and `plan/legend.ts` does not fetch and
   * could not name it.
   *
   * Printed muted and after the name, so it reads as a footnote on the reading
   * rather than as part of what the indicator is called.
   */
  noteFor?: (plotId: string) => string | undefined;
  /**
   * A word to print for a plot **instead of** its number at this bar, or
   * `undefined` to keep the number. For a plot whose reading is a label rather
   * than a value — Inside / Outside Bars reads `Inside`, `Outside`, or nothing
   * (`""`) on a bar that is neither. Its series carries no column, so the plan
   * layer can only print blank.
   */
  readingFor?: (plotId: string, index: number) => string | undefined;
  /** How many bars are loaded, which is what "the last one" means. */
  barCount: number;
  /** Names the price row — a candle is the instrument, not the word `ohlc`. */
  symbol?: string;
  controls?: LegendControls;
  /**
   * CSS pixels from the chart's left edge to the plot areas — a left Y axis's
   * width. The block starts past it, or the axis's labels and price tags draw
   * over the readings (owner, 2026-10-30).
   */
  left?: number;
}

/**
 * Left of the plot area, and clear of the top.
 *
 * 8px in rather than 0: flush to the corner the first row would touch both the
 * Y axis' top tick label and the pane above's seam, and a block that touches a
 * gridline reads as attached to it.
 */
const INSET = 8;
/**
 * A gap kept short: K/M/B when large (`legendValue` already
 * does), and two significant figures below 1 — `-0.0073`, not `-0.00725` — the
 * corner has room for a reading, not for its noise.
 */
function gapValue(d: number): string {
  return Math.abs(d) >= 1 || d === 0 ? legendValue(d) : String(Number(d.toPrecision(2)));
}

/** Clear of the right-hand Y axis: its 50px label floor plus the tick. */
const CORNER_RIGHT = 64;

/**
 * The one family every projected row belongs to.
 *
 * A constant rather than a lookup, because it names nothing in the catalog:
 * these rows continue *other* indicators, and grouping them by what they
 * continue is precisely what produces the doubled legend this replaces.
 *
 * It carries no controls. The rows are derived on every build and exist in no
 * workspace, so a ✕ would ask the store to remove a plot it has never seen and
 * a ⚙ would offer to re-parameterise one that has no parameters of its own.
 * They are turned off where they were turned on — Settings, *Project forward*.
 */
const FORWARD_FAMILY = { key: "~fwd", label: "Forward", removable: false } as const;

export function ValueLegend({
  panes,
  planFor,
  values,
  index,
  noteFor,
  readingFor,
  barCount,
  symbol,
  controls,
  left = 0,
}: ValueLegendProps) {
  /**
   * Which pane's block has the pointer over it.
   *
   * The controls appear on hover rather than permanently: three glyphs on each
   * of seven rows is more furniture than price action on a busy chart, and the
   * numbers are what the block is for. Per pane rather than one flag, so
   * reaching for the price pane's × does not light up the volume pane's.
   */
  const [over, setOver] = useState<string | null>(null);

  // The last loaded bar when the pointer is on none. `barCount - 1` rather than
  // a clamp of `index`: the pointer being past the right edge is the ordinary
  // case, since there is margin there by design, and it should read the same as
  // the pointer being off the chart entirely.
  const at = index ?? barCount - 1;
  if (at < 0) return null;

  const bands = stackFractions(paneWeights(panes));

  return (
    <>
      {panes.map((pane, i) => {
        const band = bands[i];
        const plan = planFor(pane.id);
        if (!band || !plan) return null;
        // A line and its own average also read the gap between them (owner,
        // 2026-10-06) — on the average's row here, and the last bar's in the
        // pane's top-right corner below.
        const gaps = gapsForPane(pane, cachedCatalog());
        const rows = legendRows(plan.series, values, at, symbol, controls?.nameOf, gaps).map((row) => {
          const word = readingFor?.(row.plotId, at);
          return word === undefined ? row : { ...row, cells: [{ value: word }] };
        });
        const corner = gaps
          .map((gap) => gapAt(plan.series, values, barCount - 1, gap))
          .filter((g): g is { line: number; average: number } => g !== undefined)
          .map((g) => g.line - g.average);
        // **Projections first, then everything measured.** They are grouped
        // rather than interleaved so the legend does not print each reading
        // twice — and they lead because the block is what the reader is
        // scanning for when a projection is on; with it off the partition is
        // empty and this is the order it always was.
        //
        // A stable partition, not a sort: within each half the plan's own draw
        // order is what decides, and reordering that would put a family's
        // outputs in an order its pane does not draw them in.
        const ordered = [
          ...rows.filter((r) => r.forward),
          ...rows.filter((r) => !r.forward),
        ];
        if (rows.length === 0) return null;
        const showing = controls && over === pane.id;
        /**
         * Hidden comes from the **contract**, not from the render plan.
         *
         * The plan is the builder's copy and is refreshed from an effect, which
         * runs *after* the render that follows a dispatch — so for one render
         * the row would still say "shown" for a plot just hidden, its button
         * would offer to hide it again, and the plot could never be brought
         * back. `pane.plots` is the state this render was produced from, so it
         * cannot be a frame behind.
         */
        const hiddenNow = new Map(
          (pane.plots ?? []).map((plot) => [plot.id, plot.hidden ?? false]),
        );

        return (
          <Fragment key={pane.id}>
          <div
            key={pane.id}
            // Measured from outside: the PIP offsets its top-left position by
            // this block's *rendered* height, because a chart with seven plots
            // makes a taller legend than one with three and a constant would
            // either overlap the readings or leave a gap. A data attribute
            // rather than a shared ref — the blocks are one per pane and the
            // reader wants a specific one, which a ref list would have to be
            // indexed into anyway.
            data-qf-legend-pane={pane.id}
            onPointerEnter={() => setOver(pane.id)}
            onPointerLeave={() => setOver(null)}
            style={{
              position: "absolute",
              top: `calc(${band.y * 100}% + ${INSET}px)`,
              left: left + INSET,
              // Kept inside its own pane. A pane with a dozen events grew a
              // block taller than the pane, and its rows ran into the pane
              // below and over that pane's legend. The rows clip inside
              // `ClippedRows`, which says how many it cut. The floor keeps one
              // row and that count showing on a very short pane.
              maxHeight: `max(calc(${band.height * 100}% - ${INSET * 2}px), 2.9em)`,
              display: "flex",
              flexDirection: "column",
              // The block itself has to hear the pointer, or the controls could
              // never be revealed — but it must not swallow a drag meant for the
              // chart under it, so only the buttons take events back.
              pointerEvents: controls ? "auto" : "none",
              // The span controls' band: above the canvas, below the dividers.
              zIndex: 4,
              fontSize: "0.68rem",
              lineHeight: 1.45,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              // No background. A panel here would hide the candles it is
              // describing, which is the objection that removed the rollover
              // tooltip — the text sits directly on the chart and earns its
              // legibility from a shadow in the background colour instead.
              textShadow: "0 0 3px var(--qf-background), 0 0 3px var(--qf-background)",
            }}
          >
            <ClippedRows>
            {ordered.map((row) => {
              const hidden = hiddenNow.get(row.plotId) ?? row.hidden;
              /**
               * **Every projected row answers to one family**, whatever it
               * continues, and that is the whole point of the grouping. Left to
               * `familyOf` they key by the *source* alias — `sma_50~fwd` parses
               * to `sma` with params `_50`, the same as `sma_50` — so each twin
               * lands beside the line it continues and the legend prints every
               * reading twice, three pixels apart, once as a fact and once as a
               * forecast.
               */
              const family = row.forward
                ? FORWARD_FAMILY
                : controls?.familyOf(row.plotId);
              const kin = family
                ? ordered.filter((other) =>
                    other.forward
                      ? family.key === FORWARD_FAMILY.key
                      : !row.forward &&
                        controls?.familyOf(other.plotId)?.key === family.key,
                  )
                : [];
              /**
               * A family drawing several rows is **headed by its own name**, and
               * its rows are indented under it.
               *
               * Without that, Chandelier's four rows read as four unrelated
               * indicators called "Long Stop", "Short Stop", "Buy" and "Sell" —
               * the family label is the only thing that says they are one
               * thing, and it was nowhere on screen. A single-row family needs
               * no header: its one row already carries the family label,
               * parameters and all, so a heading would print the same words
               * twice.
               *
               * Counted over `rows` rather than over the catalog, because the
               * skips decide this — a family whose other outputs are all fills
               * draws one row and reads as a single-plot indicator, which it
               * effectively is.
               */
              const grouped = kin.length > 1;
              const first = kin[0]?.plotId === row.plotId;
              /**
               * The heading's eye reads **all hidden** as hidden, and anything
               * else as shown — so with one line hidden by hand, it offers to
               * hide the rest rather than to show the one.
               */
              const familyHidden =
                grouped && kin.every((other) => hiddenNow.get(other.plotId) ?? other.hidden);
              return (
                <Fragment key={row.plotId}>
                  {grouped && first && (
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        opacity: familyHidden ? 0.4 : 1,
                      }}
                    >
                      <span style={{ color: "var(--qf-text-secondary)", fontWeight: 600 }}>
                        {family!.label}
                      </span>
                      {noteFor?.(row.plotId) && (
                        <span style={{ color: "var(--qf-text-muted)", fontWeight: 400 }}>
                          {noteFor(row.plotId)}
                        </span>
                      )}
                      {/* The family's own controls, on the family's own line.
                          They sat on its first *plot* while there was nothing
                          else to hang them from, which offered to remove
                          "Chandelier" from a row labelled "Long Stop". */}
                      {showing && !row.forward && (
                        <span style={{ display: "flex", gap: 2 }}>
                          <LegendButton
                            label={`${familyHidden ? "Show" : "Hide"} ${family!.label}`}
                            onClick={() => controls.onToggleFamily(row.plotId, !familyHidden)}
                          >
                            {familyHidden ? "○" : "●"}
                          </LegendButton>
                          <LegendButton
                            label={`Settings for ${family!.label}`}
                            onClick={() => controls.onEdit(row.plotId)}
                          >
                            ⚙
                          </LegendButton>
                          {/* Volume has no ✕ here for the reason it has none in
                              the picker: `removeIndicator` refuses a pane that
                              is not an indicator's, so the button would spend a
                              save and change nothing. */}
                          {family!.removable && (
                            <LegendButton
                              label={`Remove ${family!.label}`}
                              onClick={() => controls.onRemove(row.plotId)}
                            >
                              ✕
                            </LegendButton>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      // The indent is what makes four rows read as one
                      // indicator's parts rather than as four indicators.
                      paddingLeft: grouped ? 12 : 0,
                      // A hidden plot is listed so it can be turned back on, and
                      // dimmed so it is not mistaken for one that is drawn.
                      opacity: hidden ? 0.4 : 1,
                    }}
                  >
                    <span style={{ color: row.color ?? "var(--qf-text-secondary)" }}>
                      {row.name}
                    </span>
                    {/* On the row itself only when the family has no heading to
                        carry it, or a multi-row family would print the same
                        footnote once per line. */}
                    {!grouped && noteFor?.(row.plotId) && (
                      <span style={{ color: "var(--qf-text-muted)" }}>{noteFor(row.plotId)}</span>
                    )}
                    {row.cells.map((cell, cellAt) =>
                      // Skipped whole, label included: "O: " with nothing after
                      // it is worse than the dash it replaced.
                      cell.value === "" ? null : (
                        <span key={cell.label ?? cellAt} style={{ color: "var(--qf-text)" }}>
                          {/* The letter is subordinate to the number it
                              introduces: four of them on one row would otherwise
                              compete with the values for the same glance. */}
                          {cell.label && (
                            <span style={{ color: "var(--qf-text-muted)" }}>{cell.label}: </span>
                          )}
                          {cell.tag && (
                            <span style={{ color: "var(--qf-text-muted)" }}>{cell.tag} </span>
                          )}
                          {/* A change is coloured by its sign — the theme's own
                              positive/negative, the pair the screener's change
                              columns read, so a red here means what it means
                              there. */}
                          <span style={tone(cell.tone)}>{cell.value}</span>
                        </span>
                      ),
                    )}
                    {showing && !row.forward && (
                      <span style={{ display: "flex", gap: 2, marginLeft: 2 }}>
                        <LegendButton
                          label={`${hidden ? "Show" : "Hide"} ${row.name}`}
                          onClick={() => controls.onToggle(row.plotId, !hidden)}
                        >
                          {hidden ? "○" : "●"}
                        </LegendButton>
                        {/* A one-row family has no heading to put these on, so
                            they stay beside its single row. */}
                        {!grouped && family && (
                          <>
                            <LegendButton
                              label={`Settings for ${row.name}`}
                              onClick={() => controls.onEdit(row.plotId)}
                            >
                              ⚙
                            </LegendButton>
                            {family.removable && (
                              <LegendButton
                                label={`Remove ${row.name}`}
                                onClick={() => controls.onRemove(row.plotId)}
                              >
                                ✕
                              </LegendButton>
                            )}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </Fragment>
              );
            })}
            </ClippedRows>
          </div>
          {/* The last bar's gap, in a fixed corner rather than at a height on
              the axis: a Δ is not a level, and a tag placed at one would read
              as a price (owner, 2026-10-06). Value only — it fits; the percent
              is in the readout.

              **Bottom-right, not top-right** (owner's screenshot, same day):
              the axis tags sit at each line's latest value, which on a rising
              line is the top of the pane, so a top corner collided with them.
              Nothing lives in the bottom corner. */}
          {corner.length > 0 && (
            <div
              aria-label="Gap to the average, last bar"
              style={{
                position: "absolute",
                top: `calc(${(band.y + band.height) * 100}% - ${INSET}px)`,
                transform: "translateY(-100%)",
                right: CORNER_RIGHT,
                zIndex: 4,
                pointerEvents: "none",
                display: "flex",
                gap: 6,
                fontSize: "0.68rem",
                fontVariantNumeric: "tabular-nums",
                textShadow: "0 0 3px var(--qf-background), 0 0 3px var(--qf-background)",
              }}
            >
              {corner.map((d, k) => (
                <span key={k} style={tone(d > 0 ? "up" : d < 0 ? "down" : undefined)}>
                  Δ {d > 0 ? "+" : ""}
                  {gapValue(d)}
                </span>
              ))}
            </div>
          )}
          </Fragment>
        );
      })}
    </>
  );
}

/**
 * A pane's rows, clipped to the room its block has, with a count of the ones
 * that did not fit.
 *
 * **Clipped, never scrolled, until asked** (owner, 2026-10-16). It scrolled under
 * the pointer, so merely passing over a short pane drew a scrollbar across the
 * chart. Now `+N more` is a button: it opens the block to scroll inside the same
 * room, and `less` closes it again. Without the count a clipped block reads as
 * complete — the reader has no way to know an indicator is listed below the edge.
 *
 * The count is the rows whose bottom is past the visible edge, so it falls as
 * the block is scrolled. Measured after every render rather than on resize
 * alone, because the block re-renders on every bar the pointer crosses and a
 * row's width, not its height, is what changes then — cheap to recount, and a
 * pane-height drag is also a render.
 */
export function ClippedRows({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [below, setBelow] = useState(0);
  const [open, setOpen] = useState(false);

  const count = () => {
    const el = ref.current;
    if (!el) return;
    const edge = el.scrollTop + el.clientHeight;
    let n = 0;
    for (const row of Array.from(el.children) as HTMLElement[]) {
      // Half a row of slack: a row cut by a pixel of rounding is on screen.
      if (row.offsetTop + row.offsetHeight / 2 > edge) n++;
    }
    setBelow((prev) => (prev === n ? prev : n));
  };

  useLayoutEffect(count);

  const toggle = () => {
    // Closing goes back to the top, so the clipped view shows the first rows.
    if (open && ref.current) ref.current.scrollTop = 0;
    setOpen(!open);
  };

  return (
    <>
      <div
        ref={ref}
        onScroll={count}
        style={{
          // Shrinks to what is left once the count below has taken its line.
          flex: "0 1 auto",
          minHeight: 0,
          overflowY: open ? "auto" : "hidden",
          scrollbarWidth: "thin",
          // Open, the block takes the wheel so it scrolls the rows, not the chart.
          pointerEvents: open ? "auto" : undefined,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          // `offsetTop` is measured from here.
          position: "relative",
        }}
      >
        {children}
      </div>
      {(open || below > 0) && (
        <button
          type="button"
          aria-expanded={open}
          title={open ? "Show only what fits" : "Show the rest — scroll inside the legend"}
          // The canvas under this pans on a press — `LegendButton`'s guard.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            toggle();
          }}
          style={MORE}
        >
          {open ? (below > 0 ? `+${below} more · less` : "less") : `+${below} more`}
        </button>
      )}
    </>
  );
}

const MORE: React.CSSProperties = {
  flex: "none",
  alignSelf: "flex-start",
  pointerEvents: "auto",
  padding: 0,
  border: "none",
  background: "transparent",
  color: "var(--qf-text-muted)",
  font: "inherit",
  textShadow: "inherit",
  textDecoration: "underline dotted",
  cursor: "pointer",
};

const UP: React.CSSProperties = { color: "var(--qf-positive)" };
const DOWN: React.CSSProperties = { color: "var(--qf-negative)" };

function tone(which: "up" | "down" | undefined): React.CSSProperties | undefined {
  return which === "up" ? UP : which === "down" ? DOWN : undefined;
}

/**
 * One control on a row.
 *
 * `stopPropagation` on the pointer down as well as the click: the canvas under
 * this runs `ZoomPanModifier`, and a press that reaches it starts a pan — so
 * without it the chart lurches sideways every time somebody hides a plot. The
 * same guard `PaneDividers` needs, for the same reason.
 */
function LegendButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      style={{
        pointerEvents: "auto",
        font: "inherit",
        // Bigger than the rows they sit on, not smaller. These were 0.62rem —
        // below the 0.68rem text — which made three already-small glyphs into
        // targets nobody could aim at. A control has to be at least as legible
        // as the label it acts on.
        fontSize: "0.85rem",
        lineHeight: 1,
        padding: "2px 5px",
        border: "none",
        borderRadius: 3,
        background: hovered ? "var(--qf-surface)" : "transparent",
        color: hovered ? "var(--qf-text)" : "var(--qf-text-muted)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
